import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useLogin } from '@/hooks/useLogin';
import { ROUTES } from '@/app/router/routes';
import { AppFooter } from '@/components/layout/AppFooter';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { useSignup } from './hooks/useSignup';
import { useOnboardingAnalytics } from './hooks/useOnboardingAnalytics';
import { AuthLayout } from './components/AuthLayout';
import { SignInCard } from './components/SignInCard';
import {
  CreateAccountCard,
  CREATE_ACCOUNT_CARD_ID,
  CREATE_ACCOUNT_FIRST_FIELD_ID,
} from './components/CreateAccountCard';
import styles from './AuthPage.module.css';

/**
 * AuthPage — combined Sign in / Create account page, served on /login and /signUp.
 *
 * Reference UI layout: the onboarding banner, then the Sign in and Create your account
 * cards side by side (stacked on mobile). Like Flutter's auth pages there is no header.
 * While the verify-email card is showing, it is shown on its own.
 *
 * Flutter source files:
 *   - presentation/web/auth/web_login/web_login_page.dart (+ web_login_forms.dart)
 *   - presentation/web/auth/web_signup/web_signup_page.dart
 *   - application/web_auth/web_login/web_login_bloc.dart
 *   - application/web_auth/web_signup/web_signup_bloc.dart
 */

function focusCreateAccount() {
  document.getElementById(CREATE_ACCOUNT_CARD_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  document.getElementById(CREATE_ACCOUNT_FIRST_FIELD_ID)?.focus({ preventScroll: true });
}

export function AuthPage() {
  const location = useLocation();
  const { isAuthorized } = useAuthStore();
  const login = useLogin();
  const signup = useSignup();
  useOnboardingAnalytics();

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string | null>(null);

  const isVerifyingEmail = signup.phase === 'verifyEmail';
  const isSignUpRoute = location.pathname === ROUTES.SIGN_UP;
  useEffect(() => {
    if (isSignUpRoute && !isVerifyingEmail) focusCreateAccount();
  }, [isSignUpRoute, isVerifyingEmail]);

  // Already signed in — but not mid-login, where useLogin decides the destination
  // (post-login redirect / MFA) once user details load, and not while the email
  // verification finishes, where useSignup navigates after the verified dialog
  if (isAuthorized && !login.isLoading && !isVerifyingEmail) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (forgotPasswordEmail !== null) {
    return (
      <div className={styles.page}>
        <ForgotPasswordForm
          onBack={() => setForgotPasswordEmail(null)}
          onSubmit={login.handleForgotPassword}
          initialEmail={forgotPasswordEmail}
        />
        <AppFooter />
      </div>
    );
  }

  return (
    <AuthLayout
      single={isVerifyingEmail}
      // Flutter WebSignUpPage: while the verify card shows, the onboarding logo logs out
      onLogoClick={isVerifyingEmail ? signup.handleLogout : undefined}
    >
      {!isVerifyingEmail && (
        <SignInCard login={login} onForgotPassword={setForgotPasswordEmail} />
      )}
      {/* keyed by phase so "Re-sign up" starts from an empty form */}
      <CreateAccountCard key={signup.phase} signup={signup} />
    </AuthLayout>
  );
}
